// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import { useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { Auth } from '../../StateManagment/Auth'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { useFirestoreLoaded } from '../../firebase/useFirestore'
import useParticipationPeriodValue from '../../hooks/useParticipationPeriodValue'
import useMsiMonetaryTotal from '../../hooks/useMsiMonetaryTotal'
import useMaterialValue from '../../hooks/useMaterialValue'
import composeTeller from '../../helpers/impactPerEuroMethodiek/composeTeller'
import buildImpactPerEuroBreakdown from '../../helpers/dataHelpers/buildImpactPerEuroBreakdown'
import { getLatestCosts } from '../../helpers/dataHelpers/impactPerEuro'
import computeImpactPerEuro from '../../helpers/impactPerEuroMethodiek/computeImpactPerEuro'
import buildLinkedMeasurementBreakdown from '../../helpers/impactPerEuroMethodiek/buildLinkedMeasurementBreakdown'
import { MINIMUM_GEKOPPELDE_METINGEN } from '../../helpers/impactPerEuroMethodiek/bepaalStatus'
import METHODIEK_VERSIE, { isBrutoVersie } from '../../helpers/impactPerEuroMethodiek/methodiekVersie'
import notAttributedText from '../../helpers/msiMonetisationHelpers/notAttributedText'
import NotCountedGroups, { AdviesRegel } from '../../components/dashboard/NotCountedGroups'
import formatEuro from '../../helpers/formatEuro'
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'

const COST_YEAR_COLUMNS = [
    { key: 'year', label: 'Jaar' },
    { key: 'totalCosts', label: 'Totale kosten' },
    { key: 'status', label: 'Status' },
    { key: 'ratio', label: 'Impact per euro' },
]

const formatRatio = (value) =>
    value === null || value === undefined
        ? '—'
        : new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)

const formatPct = (value) =>
    value === null || value === undefined
        ? 'niet opgegeven'
        : `${new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 }).format(value)}%`

/**
 * Dekkingsregel bij een stroom van de teller (§ 6.3): hoeveel metingen niet
 * doorgerekend konden worden, en waarom.
 * @param {object} props
 * @param {number|null|undefined} props.count - Aantal niet doorgerekend; niets tonen bij 0 of onbekend
 * @param {Object<string, number>} [props.reasons] - Dezelfde telling per reden
 */
const NotAttributedNote = ({ count, reasons }) => (count > 0
    ? <span className='participation-detail-component-note'>{notAttributedText(count, reasons)}</span>
    : null)

/**
 * Detailpagina achter de "Impact per euro"-KPI. Toont het kengetal volgens
 * de methodiek (v1.2: teller toegerekend volgens de MSI-rekenregels): de boekjaar-gescopete teller (cohortprincipe § 1), de
 * noemer-opbouw (kosten + ondersteunende vrijwilligersinzet, incidentele
 * correctie, inkomsten-ondergrens — § 2/§ 4), dekking en kosten/opbrengsten-
 * verhouding (§ 5.1/§ 2.4), de naar-rato-lezing (§ 3), de beperkingen (§ 6)
 * en het vastleggen van definitieve kengetallen (§ 5.3: geen herberekening
 * met terugwerkende kracht).
 */
const ImpactPerEuroDetail = () => {
    const history = useHistory()
    /** @param {string} pad */
    const naarScherm = (pad) => history.push(pad)
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)
    // Loaded-vlag: de MSI-fetch wacht tot de jaargrenzen bekend zijn — anders
    // vertrekt eerst een all-time sweep die direct wordt weggegooid.
    const [compagnyCosts, costsLoaded] = useFirestoreLoaded('CompagnyCosts')

    const latest = getLatestCosts(Array.isArray(compagnyCosts) ? compagnyCosts : [])
    const jaar = latest ? Number(latest.Year) : null

    const startMs = jaar !== null ? new Date(jaar, 0, 1).getTime() : undefined
    const endMs = jaar !== null ? new Date(jaar + 1, 0, 1).getTime() - 1 : undefined

    // Methodiek v1.2: de teller rekent met de toegerekende waarde (rekenregels MSI).
    // AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
    const {
        attributedValue: participationValue,
        notAttributedCount: participationNotAttributed,
        notAttributedReasons: participationNotAttributedReasons,
        linkedMeasurementCount: participationCount,
        participation,
        loading: parametersLoading,
        error: parametersError,
    } = useParticipationPeriodValue(jaar)
    const {
        attributedTotal: msiTotal,
        notAttributedCount: msiNotAttributed,
        notAttributedReasons: msiNotAttributedReasons,
        perEffect: msiPerEffect,
        linkedPersonaDeltaCount,
        loading: msiLoading,
        error: msiError,
    } = useMsiMonetaryTotal({ startMs, endMs, enabled: costsLoaded })

    const {
        value: materialValue,
        unknownCount: materialUnknownCount,
        error: materialError,
    } = useMaterialValue({ startMs, endMs })

    const loading = parametersLoading || msiLoading
    const error = parametersError || msiError || materialError

    // Samenstelling via de gedeelde helper: welke stromen meetellen en wat als
    // gekoppelde meting telt staat op één plek, zodat deze pagina en de
    // dashboardkaart niet uit elkaar kunnen lopen.
    const { teller, linkedMeasurementCount } = composeTeller({
        participationValue,
        participationCount,
        msiValue: msiTotal,
        msiCount: linkedPersonaDeltaCount,
        materialValue,
    })

    const vastgelegdSnapshot = latest?.Kengetal && Number.isFinite(Number(latest.Kengetal.Value))
        ? latest.Kengetal
        : null

    const result = useMemo(() => {
        if (!latest || teller === null) return null
        return computeImpactPerEuro({ teller, costsRow: latest, linkedMeasurementCount })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latest, teller, linkedMeasurementCount])

    const breakdown = useMemo(
        () => buildImpactPerEuroBreakdown({ result, compagnyCosts: Array.isArray(compagnyCosts) ? compagnyCosts : [] }),
        [result, compagnyCosts],
    )

    // Welke metingen de drempel van twintig halen en welke niet, mét de reden.
    // Zonder die uitsplitsing is een te laag totaal niet te onderscheiden van
    // een organisatie die niets gemeten heeft.
    // AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
    const koppelingen = useMemo(
        () => buildLinkedMeasurementBreakdown({ participation, msiPerEffect }),
        [participation, msiPerEffect],
    )

    // Vastgelegd snapshot wint van de live berekening (§ 5.3).
    const heroKengetal = vastgelegdSnapshot ? Number(vastgelegdSnapshot.Value) : (result?.kengetal ?? null)
    const heroLabel = vastgelegdSnapshot
        ? `Impact per euro v${vastgelegdSnapshot.MethodiekVersie || '?'} (definitief, ${jaar}) · vastgelegd${isBrutoVersie(vastgelegdSnapshot.MethodiekVersie) ? ' · bruto' : ''}`
        : (result?.label ?? 'Impact per euro')

    const kanVastleggen = Boolean(
        latest && !vastgelegdSnapshot && result && result.status === 'definitief' && result.kengetal !== null,
    )

    const vastleggenHandler = () => {
        if (!kanVastleggen || !latest?.docid) return

        db.collection('CompagnyCosts')
            .doc(latest.docid)
            .update({
                Kengetal: {
                    Value: result.kengetal,
                    RuweRatio: result.ruweRatio,
                    Teller: teller,
                    Noemer: result.noemer.noemer,
                    MethodiekVersie: METHODIEK_VERSIE,
                    Status: 'definitief',
                    DekkingPct: result.dekkingPct,
                    KostenOpbrengstenVerhouding: result.kostenOpbrengstenVerhouding,
                    LinkedMeasurementCount: linkedMeasurementCount,
                    VastgelegdOp: firebase.firestore.FieldValue.serverTimestamp(),
                    VastgelegdDoor: auth?.ID || '',
                },
            })
            .then(() => setSaved('flex'))
            .catch((err) => alert(`Vastleggen mislukt: ${err.message}`))
    }

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading='Impact per euro'
                    description={`Het kengetal volgens de Impact per euro-methodiek v${METHODIEK_VERSIE}: de maatschappelijke waarde die met elke bestede euro samenhangt, berekend over één boekjaar.`}
                    AI={false}
                />

                {loading && <p className='participation-detail-status'>Rekenparameters laden…</p>}

                {!loading && error && (
                    <p className='participation-detail-error'>
                        De rekenwaarden konden niet worden geladen. Vernieuw de pagina of probeer het later opnieuw.
                    </p>
                )}

                {!loading && !error && (
                    <div className='participation-detail'>
                        <div className='participation-detail-hero'>
                            <span className='participation-detail-hero-label'>Impact per euro</span>
                            <span className='participation-detail-hero-value'>{formatRatio(heroKengetal)}</span>
                            <span className='participation-detail-hero-meta'>{heroLabel}</span>
                            {!vastgelegdSnapshot && result?.status === 'onvoldoende' && result.reden && (
                                <>
                                    <span className='impact-per-euro-warning'>{result.reden}</span>
                                    <AdviesRegel code={result.redenCode} onNavigate={naarScherm} />
                                </>
                            )}
                        </div>

                        <div className='participation-detail-section'>
                            <h2>De berekening</h2>
                            <div className='impact-per-euro-formula'>
                                <div className='impact-per-euro-formula-part'>
                                    <span className='num'>{teller !== null ? formatEuro(teller) : '—'}</span>
                                    <span className='lbl'>Maatschappelijke waarde{jaar !== null ? ` (${jaar})` : ''}</span>
                                </div>
                                <span className='impact-per-euro-formula-op'>÷</span>
                                <div className='impact-per-euro-formula-part'>
                                    <span className='num'>{result?.noemer ? formatEuro(result.noemer.noemer) : '—'}</span>
                                    <span className='lbl'>Totale inzet{jaar !== null ? ` (${jaar})` : ''}</span>
                                </div>
                                <span className='impact-per-euro-formula-op'>=</span>
                                <div className='impact-per-euro-formula-part impact-per-euro-formula-result'>
                                    <span className='num'>{formatRatio(result?.kengetal ?? null)}</span>
                                    <span className='lbl'>{`Impact per euro (afgerond op € 0,05 naar beneden${result?.ruweRatio !== null && result?.ruweRatio !== undefined ? `, onafgerond ${formatRatio(result.ruweRatio)}` : ''})`}</span>
                                </div>
                            </div>
                            {!latest && (
                                <p className='participation-detail-empty'>
                                    Er zijn nog geen kosten ingevuld. Voeg de jaarlijkse kosten toe bij de
                                    instellingen om de impact per euro te berekenen.
                                    <AdviesRegel code='geen-kosten' onNavigate={naarScherm} />
                                </p>
                            )}
                        </div>

                        <div className='participation-detail-section'>
                            <div className='participation-detail-section-head'>
                                <h2>Maatschappelijke waarde (teller)</h2>
                                <button
                                    type='button'
                                    className='impact-per-euro-link'
                                    onClick={() => history.push(`/${client}/maatschappelijke-waarde`)}
                                >
                                    Volledige opbouw →
                                </button>
                            </div>
                            <p className='participation-detail-section-sub'>
                                Alleen verandering die bínnen dit boekjaar is gemeten, toegerekend aan de
                                interventie over de looptijd van het effect.
                            </p>
                            <div className='participation-detail-components'>
                                <div className='participation-detail-component-row'>
                                    <span className='lbl'>
                                        Participatieladder (periode-verandering)
                                        <NotAttributedNote count={participationNotAttributed} reasons={participationNotAttributedReasons} />
                                    </span>
                                    <span className='val'>{participationValue !== null ? formatEuro(participationValue) : '—'}</span>
                                </div>
                                <div className='participation-detail-component-row'>
                                    <span className='lbl'>
                                        MSI-effecten (periode-verandering)
                                        <NotAttributedNote count={msiNotAttributed} reasons={msiNotAttributedReasons} />
                                    </span>
                                    <span className='val'>{msiTotal !== null ? formatEuro(msiTotal) : '—'}</span>
                                </div>
                                <div className='participation-detail-component-row'>
                                    <span className='lbl'>
                                        Materialen en coachadviezen (maatschappelijke besparing)
                                        {materialUnknownCount > 0 && (
                                            <span className='participation-detail-component-note'>
                                                {materialUnknownCount === 1
                                                    ? '1 registratie kon niet worden doorgerekend'
                                                    : `${materialUnknownCount} registraties konden niet worden doorgerekend`}
                                            </span>
                                        )}
                                    </span>
                                    <span className='val'>{materialValue !== null ? formatEuro(materialValue) : '—'}</span>
                                </div>
                                <div className='participation-detail-component-row participation-detail-component-total'>
                                    <span className='lbl'>Totaal</span>
                                    <span className='val'>{teller !== null ? formatEuro(teller) : '—'}</span>
                                </div>
                            </div>
                        </div>

                        {koppelingen && (
                            <div className='participation-detail-section'>
                                <div className='participation-detail-section-head'>
                                    <h2>Gekoppelde metingen</h2>
                                    <span className='impact-per-euro-threshold'>
                                        {`${koppelingen.total} van de ${MINIMUM_GEKOPPELDE_METINGEN}`}
                                    </span>
                                </div>
                                <p className='participation-detail-section-sub'>
                                    Een meting telt mee zodra zij de teller kan voeden: dezelfde deelnemer
                                    op twee momenten gemeten, op iets waar de Meetstandaard bedragen voor
                                    heeft.
                                </p>

                                <div className='participation-detail-components'>
                                    <h3 className='participation-detail-component-group'>Telt mee</h3>
                                    {koppelingen.counted.length === 0 ? (
                                        <div className='participation-detail-component-row'>
                                            <span className='lbl'>Nog geen gekoppelde metingen in dit boekjaar</span>
                                            <span className='val'>0</span>
                                        </div>
                                    ) : koppelingen.counted.map((rij) => (
                                        <div className='participation-detail-component-row' key={rij.key}>
                                            <span className='lbl'>{rij.label}</span>
                                            <span className='val'>{rij.count}</span>
                                        </div>
                                    ))}
                                </div>

                                {koppelingen.notCounted.length > 0 && (
                                    <div className='participation-detail-components'>
                                        <h3 className='participation-detail-component-group'>Telt niet mee</h3>
                                        {/* Per reden één kop met wat je eraan kunt doen; de
                                            effecten met die reden staan eronder (inbox #347). */}
                                        <NotCountedGroups rows={koppelingen.notCounted} onNavigate={naarScherm} />
                                    </div>
                                )}
                            </div>
                        )}

                        {result?.noemer && (
                            <div className='participation-detail-section'>
                                <h2>Totale inzet (noemer)</h2>
                                <p className='participation-detail-section-sub'>
                                    Kosten conform de jaarrekening plus onbetaalde ondersteunende inzet.
                                    Uren van deelnemers tellen niet mee; hun waarde zit al in de teller.
                                </p>
                                <div className='participation-detail-components'>
                                    <div className='participation-detail-component-row'>
                                        <span className='lbl'>Kosten conform jaarrekening</span>
                                        <span className='val'>{formatEuro(result.noemer.componenten.kosten)}</span>
                                    </div>
                                    {result.noemer.componenten.incidenteleCorrectie > 0 && (
                                        <div className='participation-detail-component-row'>
                                            <span className='lbl'>{`Incidentele posten (gecorrigeerd${latest?.IncidentalNote ? `: ${latest.IncidentalNote}` : ''})`}</span>
                                            <span className='val'>− {formatEuro(result.noemer.componenten.incidenteleCorrectie)}</span>
                                        </div>
                                    )}
                                    {result.noemer.componenten.vrijwilligersWaarde > 0 && (
                                        <div className='participation-detail-component-row'>
                                            <span className='lbl'>{`Ondersteunende vrijwilligers (${latest?.SupportVolunteerHours} uur × ${formatEuro(result.noemer.componenten.uurtarief)})`}</span>
                                            <span className='val'>{formatEuro(result.noemer.componenten.vrijwilligersWaarde)}</span>
                                        </div>
                                    )}
                                    <div className='participation-detail-component-row participation-detail-component-total'>
                                        <span className='lbl'>Totale inzet</span>
                                        <span className='val'>{formatEuro(result.noemer.noemer)}</span>
                                    </div>
                                </div>
                                {result.noemer.incidenteleOnderDrempel && (
                                    <p className='impact-per-euro-warning'>
                                        De gecorrigeerde incidentele post is niet groter dan 10% van de kosten;
                                        de methodiek staat correctie alleen boven die drempel toe.
                                    </p>
                                )}
                                {result.noemer.ondergrensToegepast && (
                                    <p className='impact-per-euro-warning'>
                                        De totale inkomsten zijn hoger dan de inzet; de noemer is gelijkgesteld
                                        aan de inkomsten (noemer-ondergrens). Er kan nooit meer geclaimd worden
                                        dan er gemeten is.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className='participation-detail-section'>
                            <h2>Context bij het kengetal</h2>
                            <div className='participation-detail-components'>
                                <div className='participation-detail-component-row'>
                                    <span className='lbl'>Dekking: aandeel organisatiekosten gedekt door gemeten activiteiten</span>
                                    <span className='val'>{formatPct(result?.dekkingPct ?? null)}</span>
                                </div>
                                <div className='participation-detail-component-row'>
                                    <span className='lbl'>Verhouding kosten / opbrengsten</span>
                                    <span className='val'>{result?.kostenOpbrengstenVerhouding ? formatRatio(result.kostenOpbrengstenVerhouding) : 'niet opgegeven'}</span>
                                </div>
                            </div>
                            {(result?.dekkingPct === null || result?.dekkingPct === undefined || result.dekkingPct < 70) && (
                                <p className='impact-per-euro-warning'>
                                    Voor benchmarkgebruik en formele verantwoording geldt een minimumdekking
                                    van circa 70%. Daaronder (of zonder opgegeven dekking) is het kengetal
                                    alleen bruikbaar mét vermelding van de dekking.
                                </p>
                            )}
                        </div>

                        {(kanVastleggen || vastgelegdSnapshot) && (
                            <div className='participation-detail-section'>
                                <h2>Vastleggen</h2>
                                {vastgelegdSnapshot ? (
                                    <p className='participation-detail-section-sub'>
                                        {`Het kengetal voor ${jaar} is vastgelegd en wordt niet meer herrekend — ook niet bij nieuwe versies van de methodiek of de Meetstandaard.`}
                                    </p>
                                ) : (
                                    <>
                                        <p className='participation-detail-section-sub'>
                                            {`Het boekjaar ${jaar} is afgesloten en de kosten zijn bevestigd. Leg het definitieve kengetal vast; daarna wordt het niet meer herrekend.`}
                                        </p>
                                        <button type='button' className='button-simple' onClick={vastleggenHandler}>
                                            Kengetal vastleggen
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        <div className='participation-detail-section'>
                            <h2>Kosten per jaar</h2>
                            <p className='participation-detail-section-sub'>
                                Gerekend wordt op het meest recente jaar. Eerdere jaren tonen alleen een
                                getal als het is vastgelegd.
                            </p>
                            <Table
                                columns={COST_YEAR_COLUMNS}
                                items={breakdown.years}
                                getItemKey={(row) => String(row.year)}
                                showSearch={false}
                                emptyMessage='Nog geen kosten ingevuld.'
                                getRowClassName={(row) => (row.isCurrent ? 'impact-per-euro-current-row' : '')}
                                renderRow={(row) => (
                                    <>
                                        <TableCell>{row.year}</TableCell>
                                        <TableCell>{formatEuro(row.totalCosts)}</TableCell>
                                        <TableCell>
                                            {row.vastgelegd
                                                ? <span className='impact-per-euro-current-pill'>{row.bruto ? 'Vastgelegd · bruto' : 'Vastgelegd'}</span>
                                                : row.isCurrent
                                                    ? <span className='impact-per-euro-current-pill'>{row.statusLabel || 'In gebruik'}</span>
                                                    : '—'}
                                        </TableCell>
                                        <TableCell className='participation-detail-table-total'>{formatRatio(row.kengetal)}</TableCell>
                                    </>
                                )}
                            />
                        </div>

                        {/* Ingeklapt: dit is verantwoording, geen terugkoppeling. Niets is
                            weggehaald — de pagina toonde acht secties tegelijk en de lezer
                            vond het antwoord daardoor niet. */}
                        <details className='impact-per-euro-verantwoording'>
                            <summary>Wat dit kengetal wel en niet zegt</summary>
                            <p>
                                Het kengetal is een evenredig aandeel, geen marginale causaliteit: een
                                bijdrage draagt naar rato bij aan de gemeten maatschappelijke waarde — niet
                                "uw euro leverde dit op". Elke financierende euro claimt zo zijn evenredige
                                deel; alle delen samen vormen het geheel.
                            </p>
                            <ul className='impact-per-euro-beperkingen'>
                                <li>Het is een gemiddelde over alle activiteiten en euro's — geen bewijs dat een specifieke euro een specifiek effect veroorzaakte.</li>
                                <li>Alleen verandering die in het boekjaar is gemeten telt mee, met de waarde over een looptijd van 1 tot 5 jaar volgens de rekenregels van de Meetstandaard (25% afname per jaar, 2,8% disconto). Die looptijd is een aanname op basis van duur en uren van de deelname, geen meting.</li>
                                <li>Deadweight en attributie zitten samen in de impactclaim van 40%, een standaardaanname van de Meetstandaard. Verdringing is nog niet verrekend.</li>
                                <li>De kengetallen per effect komen ontdubbeld uit de Meetstandaard; de optelling over effecten steunt op die garantie.</li>
                                <li>Monetarisering blijft een model: een onderbouwde schatting op basis van publieke bronnen, geen meting van geldstromen.</li>
                            </ul>
                        </details>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ImpactPerEuroDetail
